import ActionFeedbackModalBody, { ButtonStyle } from '@/components/ActionFeedbackModal/ActionFeedbackModalBody';
import CourseSection from '@/components/CourseSection';
import EmptySection from '@/components/EmptySection';
import Loader from '@/components/Loader';
import Constants from '@/Constants';
import { LessonContentTO } from '@/models/LessonContentTO';
import { LessonData } from '@/models/LessonData';
import { analyticsService, PlaylistEvents } from '@/services/AnalyticsService';
import { courseService } from '@/services/CourseService';
import { ApplicationState } from '@/store';
import { closeModal, openModal } from '@/store/common/actions';
import { addContentToCourseRequest, associateCourseReset, createCourseReset, myCoursesClear } from '@/store/list/action';
import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Menu from '../Menu';
import './style.scss';

const MyLists: React.FC = () => {
    const [isNavigatingBack, setIsNavigatingBack] = useState<boolean>(false);
    const dispatch = useDispatch();
    const history = useHistory();

    const {
        strings,
        currentContent,
        loading,
        hasError,
        success,
        isUserCreatingList,
        isUserAssociatingList,
        selectedCourseForInsertion,
        listMyCourses,
        loadingMyCourses,
        totalMyCourses,
    } = useSelector((state: ApplicationState) => {
        return {
            strings: state.locale.data,
            currentContent: state.itens.currentData.content,
            loading: state.lists.loading,
            loadingMyCourses: state.lists.listData['listMyCourseStudentView'].loading,
            hasError: state.lists.hasError,
            success: state.lists.success,
            isUserCreatingList: state.lists.isUserCreatingList,
            isUserAssociatingList: state.lists.isUserAssociatingList,
            totalMyCourses: state.lists.listData['listMyCourseStudentView'].total,
            selectedCourseForInsertion: state.lists.selectedCourseForInsertion,
            listMyCourses: state.lists.listData.listMyCourseStudentView.list,
        };
    });

    const isContentInCourse = (): boolean => {
        const courseAux = listMyCourses.filter((list) => list.course.id === (selectedCourseForInsertion && selectedCourseForInsertion.id));
        if (courseAux && courseAux.length > 0) {
            return courseService.isContentInCourse(courseAux[0].course, currentContent);
        }
        return false;
    };

    const isAddCourseAllowed: boolean = !isContentInCourse();

    const handleCreateNewListClick = () => {
        if (selectedCourseForInsertion && selectedCourseForInsertion.id && isAddCourseAllowed) {
            addContentToCourse();
        } else {
            localStorage.setItem(Constants.MY_CONTENT_ID_TO_REDIRECT_BACK, String(currentContent.id));
            history.push('/mine/courses/create');
            dispatch(closeModal(Constants.modal.MORE_OPTIONS));
        }
    };

    const addContentToCourse = () => {
        const lessonData: LessonData[] = [
            {
                mediaLesson: {
                    '@class': '.LessonContentTO',
                    id: currentContent.id,
                    course: { id: selectedCourseForInsertion && selectedCourseForInsertion.id },
                } as LessonContentTO,
            },
        ];

        dispatch(addContentToCourseRequest(lessonData));
    };

    const onYesHandler = () => {
        backToMoreOptionsMenu();
    };

    const onNoHandler = () => {
        setIsNavigatingBack(false);
    };

    const onSuccessHandler = () => {
        dispatch(createCourseReset());
        dispatch(associateCourseReset());
        analyticsService.logEvent(PlaylistEvents.CREATE_STUDENT_PLAYLIST);
    };

    const handleGoBackClick = () => {
        if (selectedCourseForInsertion && selectedCourseForInsertion.id && isAddCourseAllowed) {
            setIsNavigatingBack(true);
        } else {
            backToMoreOptionsMenu();
        }
    };

    const backToMoreOptionsMenu = () => {
        dispatch(
            openModal({
                id: Constants.modal.MORE_OPTIONS,
                layoutModal: <Menu />,
                isVisible: true,
                isCloseAllowed: true,
            }),
        );
    };

    const dismissModalHandler = () => {
        dispatch(closeModal(Constants.modal.MORE_OPTIONS));
    };

    useEffect(() => {
        return () => {
            dispatch(myCoursesClear());
        };
    }, []);

    return (
        <>
            {isUserCreatingList || isNavigatingBack || isUserAssociatingList ? (
                <ActionFeedbackModalBody
                    buttonStyle={
                        isNavigatingBack
                            ? ButtonStyle.PromptButtonStyle
                            : hasError && !loading
                            ? ButtonStyle.ActionButtonStyle
                            : ButtonStyle.NoButtonStyle
                    }
                    loading={loading}
                    message={isNavigatingBack ? strings.LEAVE_ACTION_WARNING : !hasError ? strings.ITEM_ADDED_TO_LIST : strings.ERROR_WHILE_SAVE_DATA}
                    modalId={Constants.modal.MORE_OPTIONS}
                    onDismiss={dismissModalHandler}
                    onNo={onNoHandler}
                    onRetry={handleCreateNewListClick}
                    onSuccess={onSuccessHandler}
                    onYes={onYesHandler}
                    success={success}
                    title={isNavigatingBack ? strings.LEAVE_ACTION_MESSAGE : !hasError ? strings.SUCCESS_WITH_EXCLAMATION : strings.OPS}
                />
            ) : (
                <div className="my-lists-modal">
                    <Modal.Header>
                        <div className="header-info">
                            <i className="material-icons-outlined align-middle" onClick={handleGoBackClick}>
                                {Constants.icons.ARROW_LEFT}
                            </i>
                            <Modal.Title>
                                <span className="title">{strings.ADD_TO_MY_LISTS}</span>
                            </Modal.Title>
                        </div>
                        <div className="brand-gradient" />
                    </Modal.Header>
                    <Modal.Body>
                        <>
                            {!loadingMyCourses && totalMyCourses === 0 && (
                                <EmptySection
                                    iconName={Constants.icons.SMS_FAILED}
                                    message={strings.MY_LISTS_EMPTY_SECTION}
                                    style={{ height: '100%' }}
                                />
                            )}

                            {loadingMyCourses && totalMyCourses === 0 && <Loader />}

                            {!loadingMyCourses && totalMyCourses > 0 && (
                                <CourseSection
                                    mode={'selection'}
                                    slideConfiguration={{ slidesToShow: 2.1, slidesToScroll: 2 }}
                                    viewType="listMyCourseStudentView"
                                />
                            )}
                        </>
                        {!isAddCourseAllowed && (
                            <div className="error-container">
                                <i className="material-icons align-middle error-icon">{Constants.icons.REPORT_PROBLEM}</i>
                                <span>{strings.COURSE_HAS_CONTENT_ERROR}</span>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button className="btn-add-new-list col-sm" onClick={handleCreateNewListClick}>
                            {selectedCourseForInsertion && selectedCourseForInsertion.id && isAddCourseAllowed
                                ? strings.CONCLUDE
                                : strings.CREATE_NEW_LIST}
                        </Button>
                    </Modal.Footer>
                </div>
            )}
        </>
    );
};

export default MyLists;
